import React, { useState } from "react";
import { Box, Divider, IconButton, Stack, TextField, Typography, Link, Grid } from "@mui/material";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Utils from "@/components/Shared/Utils";
import UICard, { UICardContent, UICardHeadingLight } from "@/components/Shared/Utils/UICard";
import { PrimaryButton, OutlinedButton } from "@/components/Shared/Utils/Buttons";
import { grey, blue } from "@mui/material/colors";
import { useNotificationContext } from "@/contexts/NotificationContext";
import { resolveApiKeys, mutateCreateApiKey, mutateRevokeApiKey, mutateResolveApiKey } from "@/services/apikeys";
import { useQueryClient } from "react-query";
import CodeIcon from '@mui/icons-material/Code';
import HttpIcon from '@mui/icons-material/Http';
import DescriptionIcon from '@mui/icons-material/Description';
import { Controller, useForm } from "react-hook-form";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(relativeTime);

export default function AccountApiKeys({ customer, user }: any) {
  const { data: apiKeys = [], refetch } = resolveApiKeys();
  const [showNewKeyForm, setShowNewKeyForm] = useState(false);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { notification, setNotification } = useNotificationContext();
  const queryClient = useQueryClient();
  
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty }
  } = useForm({
    defaultValues: {
      name: ""
    }
  });

  const handleCopyKey = async (key: string) => {
    const data = await mutateResolveApiKey(key);

    navigator.clipboard.writeText(data?.data?.apikey);
    setNotification({
      ...notification,
      message: "API key copied to clipboard",
      severity: "success",
      open: true
    });
  };

  const generateNewKey = async (formData: { name: string }) => {
    setIsLoading(true);
    
    try {
      const response = await mutateCreateApiKey(formData.name);
      const newKey = response?.data?.key?.key;
      
      if (newKey) {
        setNewlyCreatedKey(newKey);
        setNotification({
          ...notification,
          message: `New API key '${formData.name}' has been created successfully.`,
          severity: "success",
          open: true
        });
        
        // Refresh API keys list
        await queryClient.invalidateQueries(["customer.api.keys"]);
      }
    } catch (error) {
      setNotification({
        ...notification,
        message: "Error Creating API Key. Please try again.",
        severity: "error",
        open: true
      });
    } finally {
      reset();
      setShowNewKeyForm(false);
      setIsLoading(false);
      refetch();
    }
  };

  const revokeKey = async (id: string, name: string) => {
    setIsLoading(true);
    
    try {
      await mutateRevokeApiKey(id);
      
      setNotification({
        ...notification,
        message: `API key '${name}' has been revoked successfully.`,
        severity: "success",
        open: true
      });
      
      // Refresh API keys list
      await queryClient.invalidateQueries(["customer.api.keys"]);
      
      // Clear newly created key if it was revoked
      if (newlyCreatedKey) {
        setNewlyCreatedKey(null);
      }
    } catch (error) {
      setNotification({
        ...notification,
        message: "Error revoking API key. Please try again.",
        severity: "error",
        open: true
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Utils.LayoutUtils.MainContainer>
      <Utils.LayoutUtils.MainWrapper>
        <Utils.LayoutUtils.MainHeading
          title="API Keys"
          description={`${customer?.customer_id || ''} - ${customer?.customer_email || user?.email}`}
        />

        {newlyCreatedKey && (
          <UICard gradient>
            <UICardHeadingLight 
              title="New API Key Created" 
              description="Make sure to copy your API key now. You won't be able to see it again!"
            />
            <UICardContent>
              <Box p={2}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    fullWidth
                    value={newlyCreatedKey}
                    variant="outlined"
                    size="small"
                    InputProps={{ readOnly: true }}
                  />
                  <IconButton onClick={() => handleCopyKey(newlyCreatedKey)} color="primary">
                    <ContentCopyIcon />
                  </IconButton>
                </Stack>
              </Box>
            </UICardContent>
          </UICard>
        )}

        <UICard gradient>
          <UICardHeadingLight 
            title="Your API Keys" 
            description="API keys provide programmatic access to your resources"
          >
            {!showNewKeyForm && (
              <PrimaryButton 
                onClick={() => setShowNewKeyForm(true)}
                disabled={showNewKeyForm || !customer}
              >
                Create New Key
              </PrimaryButton>
            )}
          </UICardHeadingLight>
          <UICardContent>
            {showNewKeyForm && (
              <Box p={2} sx={{ bgcolor: grey[50] }}>
                <Typography variant="subtitle2" fontSize={14} gutterBottom>Create new API key</Typography>
                <Box component="form">
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="name"
                        control={control}
                        rules={{ required: "Key name is required" }}
                        render={({ field }) => (
                          <Utils.FormUtils.PrimaryInput
                            {...field}
                            label="Key Name"
                            placeholder="e.g., Production, Development, etc."
                            error={!!errors.name}
                            helperText={errors.name?.message as string}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} display="flex" gap={2} mt={1}>
                      <Utils.AsyncButton
                        text="Generate Key"
                        disabled={isLoading || !isDirty}
                        isLoading={isLoading}
                        callback={handleSubmit(generateNewKey)}
                      />
                      <OutlinedButton onClick={() => {
                        setShowNewKeyForm(false);
                        reset();
                      }}>
                        Cancel
                      </OutlinedButton>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            )}
            
            <Divider />
            
            {apiKeys.length > 0 ? (
              <Box>
                {apiKeys.map((apiKey, index) => (
                  <React.Fragment key={apiKey.id}>
                    <Box p={2}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="subtitle2" fontWeight={500}>{apiKey.name}</Typography>
                          <Typography variant="body2" fontSize={13} color="textSecondary">
                            Created: {apiKey.created_at} • Last used: {apiKey.issued_at !== null ? dayjs(apiKey.issued_at).fromNow() : 'Never'}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={1}>
                          <OutlinedButton 
                            sx={{ color: "error.main" }}
                            onClick={() => revokeKey(apiKey.id, apiKey.name)}
                            disabled={isLoading}
                          >
                            Revoke
                          </OutlinedButton>
                          <OutlinedButton 
                            onClick={() => handleCopyKey(apiKey.id)}
                            startIcon={<ContentCopyIcon fontSize="small" />}
                          >
                            Copy
                          </OutlinedButton>
                        </Stack>
                      </Stack>
                    </Box>
                    {index < apiKeys.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </Box>
            ) : (
              <Box p={3} textAlign="center">
                <Typography color="textSecondary" fontSize={13}>You don't have any API keys yet</Typography>
              </Box>
            )}
          </UICardContent>
        </UICard>

        <UICard gradient sx={{ mt: 2 }}>
          <UICardHeadingLight 
            title="API Documentation" 
            description="Learn how to use VNETWORK OpenAPI"
          />
          <UICardContent>
            <Box p={2}>
              <Typography variant="h6" fontSize={16} fontWeight={500} gutterBottom>
                VNETWORK OpenAPI
              </Typography>
              
              <Typography variant="body2" fontSize={13} paragraph>
                This collection provides RESTful API endpoints for interacting with VNETWORK services. It supports token-based
                authentication (Bearer Token) and includes various resources for managing cloud infrastructure, such as instances,
                storage, and network configurations. Use this collection to perform standard CRUD operations, retrieve system
                information, and automate infrastructure tasks.
              </Typography>
              
              <Box sx={{ my: 2, px: 2, py: 1.5, bgcolor: grey[50], borderLeft: `4px solid ${blue[700]}` }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <CodeIcon fontSize="small" color="primary" />
                  <Typography variant="subtitle2" fontSize={14}>
                    Authorization
                  </Typography>
                </Stack>
                <Typography variant="body2" fontSize={13} mt={1}>
                  Bearer Token: <code>{"{OPENAPI_TOKEN}"}</code>
                </Typography>
                <Typography variant="body2" fontSize={13} mt={1}>
                  Your API keys can be used as Bearer Tokens in the format: <code>Bearer YOUR_API_KEY_HERE</code>
                </Typography>
              </Box>
              
              <Box sx={{ my: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                  <HttpIcon fontSize="small" color="primary" />
                  <Typography variant="subtitle2" fontSize={14}>
                    API Endpoints
                  </Typography>
                </Stack>
                <Typography variant="body2" fontSize={13} gutterBottom>
                  Base URL: <code>https://openapi.vnetwork.vn/v1</code>
                </Typography>
                <Typography variant="body2" fontSize={13} gutterBottom>
                  Example endpoint: <code>https://openapi.vnetwork.vn/v1/instances</code>
                </Typography>
                <Typography variant="body2" fontSize={13}>
                  This endpoint retrieves a list of all available instances associated with the authenticated account. 
                  It supports token-based access and returns detailed information about each instance, including ID, 
                  status, type, and resource allocation.
                </Typography>
              </Box>
              
              <Box sx={{ my: 2, px: 2, py: 1.5, bgcolor: grey[50], borderRadius: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                  <CodeIcon fontSize="small" color="primary" />
                  <Typography variant="subtitle2" fontSize={14}>
                    CURL Testing Example
                  </Typography>
                </Stack>
                <Typography variant="body2" fontSize={13} mb={1}>
                  Test your API key with a simple CURL command:
                </Typography>
                <Box sx={{ bgcolor: grey[900], color: 'white', p: 1.5, borderRadius: 1, fontFamily: 'monospace', fontSize: 12, overflowX: 'auto', position: 'relative' }}>
                  {`curl -X GET https://openapi.vnetwork.vn/v1/instances \\
  -H "Authorization: Bearer YOUR_API_KEY_HERE" \\
  -H "Content-Type: application/json"`}
                  <IconButton 
                    size="small" 
                    sx={{ position: 'absolute', top: 5, right: 5, color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
                    onClick={() => {
                      navigator.clipboard.writeText(`curl -X GET https://openapi.vnetwork.vn/v1/instances -H "Authorization: Bearer YOUR_API_KEY_HERE" -H "Content-Type: application/json"`);
                      setNotification({
                        ...notification,
                        message: "CURL command copied to clipboard",
                        severity: "success",
                        open: true
                      });
                    }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Typography variant="body2" fontSize={13} mt={1}>
                  Replace <code>YOUR_API_KEY_HERE</code> with one of your API keys from above.
                </Typography>
              </Box>
              
              <Box sx={{ my: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                  <DescriptionIcon fontSize="small" color="primary" />
                  <Typography variant="subtitle2" fontSize={14}>
                    Documentation Resources
                  </Typography>
                </Stack>
                <Typography variant="body2" fontSize={13} paragraph>
                  For security reasons, API keys should be stored securely and never exposed in client-side code or public repositories.
                </Typography>
                <Link 
                  href="https://postman.vnetwork.dev/view/4429080/UV5dduP8" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  underline="hover"
                  color={blue[900]}
                  sx={{ fontSize: 13 }}
                >
                  View complete API documentation on Postman
                </Link>
              </Box>
            </Box>
          </UICardContent>
        </UICard>
      </Utils.LayoutUtils.MainWrapper>
    </Utils.LayoutUtils.MainContainer>
  );
} 